"use client";

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatCurrency } from "@/lib/format";
import { format, subDays } from "date-fns";
import { Clock, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface RollingHoursSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weeklyLimit: number;
}

interface DayData {
  date: string;
  dateFormatted: string;
  totalHours: number;
  totalEarnings: number;
  blockCount: number;
  isFallingOff: boolean;
  isToday: boolean;
}

function formatHours(hours: number): string {
  const h = Math.floor(hours);
  // Round to nearest 15-minute increment (0, 15, 30, 45)
  const rawMinutes = (hours - h) * 60;
  const m = Math.round(rawMinutes / 15) * 15;
  // Handle case where rounding pushes to 60 minutes
  if (m === 60) {
    return `${h + 1}h`;
  }
  if (h === 0 && m === 0) return "0h";
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function RollingHoursSheet({ open, onOpenChange, weeklyLimit }: RollingHoursSheetProps) {
  // Capture timestamp when sheet opens to avoid re-querying on every render
  const [queryTimestamp, setQueryTimestamp] = useState<number | null>(null);

  useEffect(() => {
    if (open && !queryTimestamp) {
      setQueryTimestamp(Date.now());
    } else if (!open) {
      setQueryTimestamp(null);
    }
  }, [open, queryTimestamp]);

  const data = useQuery(
    api.dashboard.getRollingHoursBreakdown,
    queryTimestamp ? {
      nowTimestamp: queryTimestamp,
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    } : "skip"
  );

  // Build all 7 days with block data
  const { allDays, hoursFallingOff } = useMemo(() => {
    const now = queryTimestamp ? new Date(queryTimestamp) : new Date();

    // Group blocks by date
    const blocksByDate = new Map<string, { hours: number; earnings: number; count: number }>();
    if (data?.blocks) {
      for (const block of data.blocks) {
        const existing = blocksByDate.get(block.date);
        if (existing) {
          existing.hours += block.hours;
          existing.earnings += block.earnings;
          existing.count += 1;
        } else {
          blocksByDate.set(block.date, {
            hours: block.hours,
            earnings: block.earnings,
            count: 1,
          });
        }
      }
    }

    // Find the oldest date with hours (this is what's releasing)
    const datesWithHours = Array.from(blocksByDate.keys()).sort();
    const oldestDateWithHours = datesWithHours.length > 0 ? datesWithHours[0] : null;

    // Generate all 7 days (today through 6 days ago)
    const days: DayData[] = [];
    for (let i = 0; i < 7; i++) {
      const date = subDays(now, i);
      const dateStr = format(date, "yyyy-MM-dd");
      const blockData = blocksByDate.get(dateStr);

      days.push({
        date: dateStr,
        dateFormatted: i === 0 ? "Today" : i === 1 ? "Yesterday" : format(date, "EEE, MMM d"),
        totalHours: blockData?.hours ?? 0,
        totalEarnings: blockData?.earnings ?? 0,
        blockCount: blockData?.count ?? 0,
        isFallingOff: dateStr === oldestDateWithHours, // Oldest day with hours
        isToday: i === 0,
      });
    }

    const fallingOffHours = oldestDateWithHours ? (blocksByDate.get(oldestDateWithHours)?.hours ?? 0) : 0;

    return { allDays: days, hoursFallingOff: fallingOffHours };
  }, [data?.blocks, queryTimestamp]);

  const hoursAvailable = data ? Math.max(0, weeklyLimit - data.totalHours) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] flex flex-col">
        <SheetHeader>
          <SheetTitle>Rolling 7-Day Hours</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{data?.totalHours ?? 0}h</p>
              <p className="text-xs text-muted-foreground">worked</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{formatHours(hoursAvailable)}</p>
              <p className="text-xs text-muted-foreground">available</p>
            </div>
          </div>

          {/* Hours releasing indicator */}
          {hoursFallingOff > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>+{formatHours(hoursFallingOff)} releasing tomorrow</span>
            </div>
          )}

          {/* Days List */}
          {!data ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {allDays.map((day) => (
                <div
                  key={day.date}
                  className={cn(
                    "p-3 rounded-lg border",
                    day.isFallingOff
                      ? "bg-emerald-500/5 border-emerald-500/30"
                      : day.blockCount > 0
                        ? "bg-card"
                        : "bg-muted/30 border-dashed"
                  )}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={cn(
                          "font-medium",
                          day.blockCount === 0 && "text-muted-foreground"
                        )}>
                          {day.dateFormatted}
                        </p>
                        {day.isFallingOff && day.blockCount > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                            releasing
                          </span>
                        )}
                      </div>
                      {day.blockCount > 0 ? (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {day.blockCount} {day.blockCount === 1 ? "block" : "blocks"}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">No blocks</p>
                      )}
                    </div>
                    {day.blockCount > 0 && (
                      <div className="text-right">
                        <p className={cn(
                          "font-semibold",
                          day.isFallingOff ? "text-emerald-600 dark:text-emerald-400" : "text-primary"
                        )}>
                          {formatHours(day.totalHours)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(day.totalEarnings)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Explanation */}
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground">
              Amazon Flex uses a rolling 7-day window (168 hours) to calculate your available hours.
              Hours from the oldest day will release tomorrow and become available again.
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
